import React, { useMemo, useRef } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { FiArrowRight, FiCheck, FiMinus, FiPlus } from "react-icons/fi";
import SEO from "../components/SEO";
import useReveal from "../hooks/useReveal";
import { SITE } from "../config/site";

const TEAL = "#0d9488", MONO = "'JetBrains Mono', monospace";

function AppMock() {
  const matrix = useMemo(() => {
    const cells = [];
    for (let i = 0; i < 140; i++) {
      const r = (i * 2654435761 % 100) / 100; // deterministic-ish sprinkle
      cells.push(r < 0.02 ? "fail" : r < 0.09 ? "warn" : "");
    }
    return cells;
  }, []);
  const bars = [[20, 120, 40], [50, 115, 45], [80, 108, 52], [110, 98, 62], [140, 86, 74], [170, 74, 86], [200, 62, 98], [230, 56, 104], [260, 48, 112], [290, 40, 120], [320, 34, 126], [350, 32, 128]];
  return (
    <div className="app-mock reveal">
      <div className="app-bar"><span className="d r" /><span className="d y" /><span className="d g" /><span className="ttl">etex 3.4.1 — proje_A.edb</span></div>
      <div className="app-body">
        <div className="app-pane l">
          <div className="app-grp">Project</div>
          <div className="app-item"><span className="ic">▾</span>proje_A.edb</div>
          <div className="app-grp">Analysis</div>
          <div className="app-item"><span className="ic">●</span>Modal</div>
          <div className="app-item active"><span className="ic">●</span>Drift check</div>
          <div className="app-item"><span className="ic">●</span>Capacity</div>
          <div className="app-item"><span className="ic">●</span>Strong column</div>
          <div className="app-grp">Reporting</div>
          <div className="app-item"><span className="ic">▸</span>Hesap cildi</div>
          <div className="app-item"><span className="ic">▸</span>Drift xlsx</div>
        </div>
        <div className="app-center">
          <div className="app-sec">Story drift — X (TBDY 4.9.1)</div>
          <div className="app-chart">
            <svg viewBox="0 0 480 180" preserveAspectRatio="none">
              <line x1="0" y1="60" x2="480" y2="60" stroke="rgba(15,30,51,0.08)" strokeWidth="0.5" />
              <line x1="0" y1="120" x2="480" y2="120" stroke="rgba(15,30,51,0.08)" strokeWidth="0.5" />
              <line x1="380" y1="0" x2="380" y2="180" stroke={TEAL} strokeDasharray="4 4" strokeWidth="0.9" opacity="0.7" />
              <text x="384" y="14" fontFamily={MONO} fontSize="9" fill={TEAL}>δ_lim 0.008</text>
              <g fill={TEAL} opacity="0.85">{bars.map(([x, y, h]) => <rect key={x} x={x} y={y} width="22" height={h} rx="1" />)}</g>
            </svg>
          </div>
          <div className="app-sec" style={{ marginTop: 4 }}>Capacity matrix — 142 × 14</div>
          <div className="app-matrix">{matrix.map((c, i) => <div key={i} className={`c${c ? " " + c : ""}`} />)}</div>
        </div>
        <div className="app-pane r">
          <h5>Run Summary</h5>
          <div className="kv"><span>Total run</span><b>8.4s</b></div>
          <div className="kv"><span>Stories</span><b>14</b></div>
          <div className="kv"><span>Columns</span><b>142</b></div>
          <h5 style={{ marginTop: 20 }}>Checks</h5>
          <div className="kv"><span>Modal</span><b className="ok">✓ pass</b></div>
          <div className="kv"><span>Drift X</span><b className="ok">✓ pass</b></div>
          <div className="kv"><span>Strong col</span><b className="ok">✓ 142/142</b></div>
          <div className="kv"><span>Capacity</span><b className="bad">✗ 2 over</b></div>
          <h5 style={{ marginTop: 20 }}>Output</h5>
          <div className="kv"><span>hesap_cildi.docx</span><b>47 pg</b></div>
          <div className="kv"><span>kontroller.xlsx</span><b>16 sht</b></div>
        </div>
      </div>
    </div>
  );
}

export default function Etex() {
  const { t } = useTranslation();
  const pageRef = useRef(null);
  useReveal(pageRef, [t]);

  const hero = t("etex.hero", { returnObjects: true });
  const features = t("etex.features.items", { returnObjects: true });
  const plans = t("etex.pricing.plans", { returnObjects: true });
  const faqs = t("etex.faq.items", { returnObjects: true });

  const softwareLd = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    name: "Etex",
    applicationCategory: "EngineeringApplication",
    operatingSystem: "Windows",
    offers: { "@type": "Offer", price: "14500", priceCurrency: "TRY" },
    publisher: { "@type": "Organization", name: SITE.name },
  };

  return (
    <div ref={pageRef}>
      <SEO title={t("etex.meta_title")} description={t("etex.meta_desc")} jsonLd={softwareLd} />

      {/* HERO */}
      <section className="page-hero blueprint" style={{ borderBottom: 0 }}>
        <div className="container ph-inner">
          <div className="approach-grid" style={{ alignItems: "end", gridTemplateColumns: "1fr 1fr" }}>
            <div>
              <span className="eyebrow reveal">{hero.eyebrow}</span>
              <h1 className="reveal d1" style={{ fontSize: "clamp(72px,12vw,180px)", margin: "14px 0 20px" }}>{hero.name}<span className="accent">.</span></h1>
              <p className="ph-lede reveal d2" style={{ maxWidth: 460 }}>{hero.lede}</p>
              <div className="reveal d2" style={{ fontFamily: MONO, fontSize: 11.5, letterSpacing: "0.08em", color: "var(--slate)", textTransform: "uppercase", margin: "18px 0 24px" }}>{hero.meta}</div>
              <div className="reveal d3" style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                <Link to="/iletisim" className="btn">{hero.cta} <FiArrowRight className="arrow" /></Link>
                <a href="#fiyat" className="btn ghost">{hero.cta2}</a>
              </div>
            </div>
            <div className="reveal d2">
              <p className="muted" style={{ fontSize: 16, lineHeight: 1.7, marginBottom: 12 }}>{hero.desc_1}</p>
              <p className="muted" style={{ fontSize: 16, lineHeight: 1.7 }}>
                {hero.desc_2_pre}<b style={{ color: "var(--teal-700)" }}>{hero.desc_2_strong1}</b>{hero.desc_2_mid}<b style={{ color: "var(--teal-700)" }}>{hero.desc_2_strong2}</b>{hero.desc_2_post}
              </p>
            </div>
          </div>

          <div className="stat-strip reveal d1">
            {hero.stats.map((s, i) => (
              <div className="s" key={i}><b>{s.num}<span className="it">{s.sup}</span></b><span>{s.lbl}</span></div>
            ))}
          </div>

          <AppMock />
        </div>
      </section>

      {/* FEATURES */}
      <section className="section">
        <div className="container">
          <span className="eyebrow reveal">{t("etex.features.eyebrow")}</span>
          <h2 className="h-lg reveal d1" style={{ margin: "14px 0 12px" }}>{t("etex.features.title")}</h2>
          <p className="muted reveal d2" style={{ maxWidth: 520 }}>{t("etex.features.intro")}</p>
          <div className="feature-grid">
            {features.map((f, i) => (
              <div className="feature-cell reveal" style={{ transitionDelay: `${(i % 3) * 0.06}s` }} key={i}>
                <div className="fn">{String(i + 1).padStart(2, "0")}</div>
                <h3>{f.h}</h3>
                <p>{f.p}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section className="section soft" id="fiyat">
        <div className="container">
          <span className="eyebrow reveal">{t("etex.pricing.eyebrow")}</span>
          <h2 className="h-lg reveal d1" style={{ margin: "14px 0 12px" }}>{t("etex.pricing.title")}</h2>
          <p className="muted reveal d2" style={{ maxWidth: 520 }}>{t("etex.pricing.intro")}</p>
          <div className="pricing">
            {plans.map((p, i) => (
              <div className={`price-card reveal${p.featured ? " feat" : ""}`} style={{ transitionDelay: `${i * 0.06}s` }} key={i}>
                <div className="ptag">{p.tag}</div>
                <div className="pname">{p.name}<span className="accent">.</span></div>
                <p className="pdesc">{p.desc}</p>
                <div className="pamt">{p.cur && <span className="cur">{p.cur}</span>}{p.amount}<span className="it">{p.amount_dec}</span></div>
                <div className="pper">{p.per}</div>
                <ul>
                  {p.features.map((f, j) => <li key={j}><FiCheck /> {f}</li>)}
                  {p.muted.map((f, j) => <li className="muted" key={`m${j}`}><FiMinus /> {f}</li>)}
                </ul>
                <Link to="/iletisim" className={`btn${p.featured ? "" : " ghost"}`}>{p.cta} <FiArrowRight className="arrow" /></Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="section">
        <div className="container">
          <div style={{ textAlign: "center" }}>
            <span className="eyebrow plain reveal" style={{ justifyContent: "center" }}>{t("etex.faq.eyebrow")}</span>
            <h2 className="h-lg reveal d1" style={{ marginTop: 12 }}>{t("etex.faq.title")}</h2>
          </div>
          <div className="faq-list">
            {faqs.map((f, i) => (
              <details className="faq reveal" key={i} open={i === 0}>
                <summary><span className="qn">{String(i + 1).padStart(2, "0")}</span>{f.q}<span className="ind"><FiPlus /></span></summary>
                <p>{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* DEMO CTA */}
      <section className="section ink cta-band">
        <div className="blueprint-on-ink" style={{ position: "absolute", inset: 0, opacity: 0.4 }} />
        <div className="container cta-inner" style={{ textAlign: "center" }}>
          <h2 className="h-xl reveal">{t("etex.demo.title_1")} <span className="accent">{t("etex.demo.title_hl")}</span> {t("etex.demo.title_2")}</h2>
          <p className="reveal d1" style={{ color: "var(--on-ink-mute)", maxWidth: 520, margin: "22px auto 30px", fontSize: 16, lineHeight: 1.65 }}>{t("etex.demo.desc")}</p>
          <div className="reveal d2" style={{ display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap" }}>
            <Link to="/iletisim" className="btn on-ink">{t("etex.demo.cta")} <FiArrowRight className="arrow" /></Link>
            <a href={SITE.whatsapp} target="_blank" rel="noopener noreferrer" className="btn ghost on-ink">{t("etex.demo.cta2")}</a>
          </div>
        </div>
      </section>
    </div>
  );
}
